import { css, html } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map';

import { TeComponent } from '../common/te-component';
import { theme } from '../common/theme';
import { booleanConverter } from '../common/converters';

import { TeAccordion } from './te-accordion';
import { TRANSITION_DURATION_FAST } from '../common/transition';
import '../te-text';
import '../te-icon';

/**
 * Accordion Item Component
 *
 * @slot - Default slot for accordion item content
 */
@customElement('te-accordion-item')
export class TeAccordionItem extends TeComponent {
  static styles = [
    ...TeComponent.styles,
    css`
      .te-accordion-item {
        margin: 0;
        padding: 0;
      }

      .title-wrapper {
        box-sizing: border-box;
        display: flex;
        justify-content: space-between;
        gap: 16px;
      }

      .title-wrapper te-text {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        width: calc(100% - 64px);
        margin: 14px 0;
      }

      te-icon {
        width: 48px;
        height: 48px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: transform ${TRANSITION_DURATION_FAST}ms linear;
      }

      .te-accordion-animated-wrapper {
        transition: max-height ${TRANSITION_DURATION_FAST}ms ease-in-out,
          padding ${TRANSITION_DURATION_FAST}ms ease-in-out;
        overflow: hidden;
      }

      .te-accordion-content-wrapper {
        height: fit-content;
        min-height: fit-content;
        padding: 16px;
        margin: 0;
        background: ${theme.colors.shade1};
      }
    `,
  ];

  @state() height?: number;
  @state() internalOpenState = false;

  /** Title text for the option */
  @property({ type: String }) heading = '';
  /** Option is disabled */
  @property({ type: Boolean, converter: booleanConverter }) disabled = false;
  /** Maximum height for slotted content */
  @property({ type: Number }) maxContentHeight?: number;
  /** Component is open, overrides openByDefault */
  @property({ type: Boolean, converter: booleanConverter }) open?: boolean;
  /** Component is open on first load */
  @property({ type: Boolean, converter: booleanConverter })
  openByDefault?: boolean;

  @query('.content') _slotContent?: HTMLSlotElement;
  @query('.te-accordion-content-wrapper') slotted?: HTMLDivElement;

  firstUpdated() {
    const parent = this.parentElement as TeAccordion;
    // If open state is defined, the component can't be internally controlled
    if (this.open !== undefined) parent.isInternallyControlled = false;

    this.internalOpenState = this.openByDefault || false;
    this.height = this.slotted?.getBoundingClientRect()?.height || 0;

    if (parent.isInternallyControlled) {
      this.internalOpenState = this.openByDefault || false;
    }
  }

  onTitleClick(e: Event) {
    if (this.disabled) return;

    const parent = this.parentElement as TeAccordion;

    const newOpenState = !this.internalOpenState;
    this.internalOpenState = newOpenState;

    if (newOpenState && parent.isInternallyControlled) {
      parent.checkInternalOpenState(this);
    } else {
      // Use controlled - send event back
      this.handleClick(e);
    }
  }

  isOpen() {
    const parent = this.parentElement as TeAccordion;
    if (parent.isInternallyControlled) {
      // Use internal state
      return this.internalOpenState;
    }
    // Use user given state
    return !!this.open;
  }

  getTitleWrapperStyle() {
    const { disabled } = this;
    return {
      borderBottom: `1px solid ${this.isOpen() ? theme.colors.transparent : theme.colors.shade2}`,
      color: disabled ? `${theme.colors.shade3}` : `${theme.colors.text}`,
      cursor: disabled ? 'unset' : 'pointer',
      paddingLeft: '16px',
    };
  }

  getContentStyle() {
    if (!this.height) return {};
    return {
      maxHeight: this.isOpen() ? `${this.height}px` : '0',
    };
  }

  getIconStyle() {
    return {
      transform: this.isOpen() ? 'rotate(-90deg)' : 'rotate(0)',
    };
  }

  getContentWrapperStyle() {
    return {
      maxHeight: this.maxContentHeight ? `${this.maxContentHeight}px` : 'unset',
      overflow: this.maxContentHeight ? 'auto' : 'unset',
    };
  }

  render() {
    return html`
      <div class="te-accordion-item">
        <div
          class="title-wrapper"
          @click=${this.onTitleClick}
          style=${styleMap(this.getTitleWrapperStyle())}
        >
          <te-text
            weight="700"
            color=${this.disabled ? theme.colors.shade3.toString() : theme.colors.text.toString()}
            class="title"
          >
            ${this.heading}
          </te-text>
          <te-icon name="triangle-left" size="10" style=${styleMap(this.getIconStyle())}></te-icon>
        </div>
        <div class="te-accordion-animated-wrapper" style=${styleMap(this.getContentStyle())}>
          <div
            class="te-accordion-content-wrapper"
            style=${styleMap(this.getContentWrapperStyle())}
          >
            <slot class="content"></slot>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'te-accordion-item': TeAccordionItem;
  }
}
